import React, { useState } from 'react';
import { Check, Zap, Crown } from 'lucide-react';
import { Badge } from '@components/ui/badge';
import { useUser } from '@clerk/clerk-react';
import { plans as ALLPLANS } from '@assets/js/plans';

const PADDLE_PLAN = process.env.REACT_APP_PADDLE_PLAN;
const plans = ALLPLANS[PADDLE_PLAN] || [];

function PricingCards() {
  const { user } = useUser();
  const [selectedPlan, setSelectedPlan] = useState('premium');
  const [billingCycle, setBillingCycle] = useState('sixMonth');

  const getCurrentPrice = (plan) => {
    switch (billingCycle) {
      case 'monthly':
        return plan.monthlyPrice;
      case 'sixMonth':
        return plan.sixMonthPrice;
      case 'yearly':
        return plan.yearlyPrice;
      default:
        return plan.monthlyPrice;
    }
  };

  const getCurrentPriceId = (plan) => {
    return plan.priceId[billingCycle] || plan.priceId.monthly;
  };

  const getSavingsPercentage = (plan) => {
    const monthly = plan.monthlyPrice;
    const current = getCurrentPrice(plan);
    if (current === monthly) return 0;
    return Math.round(((monthly - current) / monthly) * 100);
  };

  const handlePayment = (plan) => {
    const email = user.primaryEmailAddress.emailAddress;
    const priceId = getCurrentPriceId(plan);

    if (window.Paddle) {
      window.Paddle.Checkout.open({
        items: [{ priceId: priceId, quantity: 1 }],
        customer: { email },
        customData: {
          user_id: user.id,
          name: user.fullName || user.username || 'Anonymous',
        },
      });
    } else {
      console.error('Paddle is not loaded');
    }
  };

  return (
    <>
      {/* Billing Toggle - Compact */}
      <div className="flex justify-center">
        <div className="inline-flex items-center bg-muted p-1.5 gap-3 rounded-lg max-w-2xl w-full mb-4">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`flex-1 px-6 py-2.5 rounded-md text-sm font-medium transition-all ${
              billingCycle === 'monthly'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}>
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('sixMonth')}
            className={`flex-1 px-6 py-2.5 rounded-md text-sm font-medium transition-all relative ${
              billingCycle === 'sixMonth'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}>
            6 Months
            <Badge
              variant="secondary"
              className="absolute -top-2 -right-2 text-xs px-1.5 py-0.5 bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
              20% OFF
            </Badge>
          </button>
          <button
            onClick={() => setBillingCycle('yearly')}
            className={`flex-1 px-6 py-2.5 rounded-md text-sm font-medium transition-all relative ${
              billingCycle === 'yearly'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}>
            Annual
            <Badge
              variant="secondary"
              className="absolute -top-2 -right-2 text-xs px-1.5 py-0.5 bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">
              47% OFF
            </Badge>
          </button>
        </div>
      </div>
      {/* Pricing Cards - More Compact */}
      <div className="grid lg:grid-cols-3 gap-4 lg:gap-6">
        {plans.map((plan, index) => {
          const currentPrice = getCurrentPrice(plan);
          const savings = getSavingsPercentage(plan);

          return (
            <div
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className={`relative bg-card rounded-2xl border-2 p-4 lg:p-6 transition-all duration-300 hover:shadow-lg cursor-pointer ${
                plan.popular
                  ? 'border-primary shadow-md shadow-primary/10 scale-[1.01]'
                  : 'border-border hover:border-primary/30'
              }`}>
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-primary to-primary/80 text-primary-foreground px-4 py-1.5 rounded-full text-xs font-bold shadow-lg flex items-center gap-1.5">
                    <Crown className="w-3 h-3" />
                    Most Popular
                  </div>
                </div>
              )}

              <div className="text-center mb-4">
                <h3 className="text-xl font-bold text-card-foreground mb-1">{plan.name}</h3>
                <p className="text-muted-foreground text-xs mb-4">{plan.description}</p>

                <div className="flex items-baseline justify-center gap-1 mb-1">
                  <span className="text-3xl font-bold text-card-foreground">${currentPrice}</span>
                  <span className="text-muted-foreground text-xs">/month</span>
                </div>

                {savings > 0 && (
                  <div className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                    Save {savings}% vs monthly
                  </div>
                )}

                <div className="mt-3 p-2.5 bg-muted/50 rounded-lg">
                  <div className="text-xs text-muted-foreground">Daily Token Limit</div>
                  <div className="text-sm font-semibold text-card-foreground flex items-center justify-center gap-1">
                    <Zap className="w-3 h-3 text-primary" />
                    {plan.dailyTokens} tokens
                  </div>
                </div>
              </div>

              <ul className="space-y-2.5 mb-4">
                {plan.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-start gap-2.5">
                    <div className="flex-shrink-0 w-4 h-4 bg-primary/15 rounded-full flex items-center justify-center mt-0.5">
                      <Check className="w-2.5 h-2.5 text-primary" />
                    </div>
                    <span className="text-card-foreground text-xs leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedPlan(plan.id);
                  handlePayment(plan);
                }}
                className={`w-full py-3 px-4 rounded-lg font-semibold text-sm transition-all duration-300 ${
                  plan.popular
                    ? 'bg-gradient-to-r from-primary to-primary/90 text-primary-foreground shadow-md hover:shadow-lg'
                    : selectedPlan === plan.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80 text-muted-foreground hover:text-foreground border border-transparent hover:border-primary/20'
                }`}>
                {selectedPlan === plan.id ? (
                  <span className="flex items-center justify-center gap-2">
                    <Check className="w-3 h-3" />
                    Selected Plan
                  </span>
                ) : (
                  `Get Started with ${plan.name}`
                )}
              </button>
            </div>
          );
        })}
      </div>
    </>
  );
}

export default PricingCards;
